/**
 * 017: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/category/category-list/columns.tsx
 *
 * Only the data, columns and labels are ours. The screen-to-template mapping is
 * specs/017-isomorphic-theme-only/contracts/screen-map.md.
 */
import type { ColumnDef } from "@tanstack/react-table";
import { AvatarCard } from "@steelyard/theme";
import { imageUrl } from "@steelyard/common";
import type { Brand } from "../../../lib/services/catalog";
import { Badge } from "rizzui";
import { PiPackageDuotone as ProductsIcon, PiEyeDuotone as EyeIcon, PiPencilSimpleDuotone as PencilIcon } from "react-icons/pi";
import { RowActions } from "../../../components/table/row-actions";
import { StatusToggle } from "../../../components/table/status-toggle";
import { brandPath } from "../../../lib/paths";

/**
 * Sizes are the template's own values: 300 for the name/logo identity cell, 150 for a short field,
 * 140 for status, 130 for actions. No selection column — no bulk action exists on this list or
 * anywhere in `apps/admin` today.
 */
export function brandColumns(
  /** Present only with write permission — the status cell is then a switch. */
  setStatus?: (brand: Brand, active: boolean) => Promise<unknown>,
): ColumnDef<Brand>[] {
  return [
    {
      id: "name",
      accessorKey: "name",
      header: "Name",
      size: 300,
      cell: ({ row }) => <AvatarCard src={row.original.logoHash ? imageUrl(row.original.logoHash) : ""} name={row.original.name} />,
    },
    {
      id: "code",
      accessorKey: "code",
      header: "Code",
      size: 150,
      cell: ({ row }) => (
        <Badge variant="flat" color="secondary" rounded="md" className="font-mono">
          {row.original.code}
        </Badge>
      ),
    },
    {
      id: "status",
      accessorKey: "isActive",
      header: "Status",
      size: 140,
      cell: ({ row }) => (
        <StatusToggle name={row.original.name} active={row.original.isActive} onChange={setStatus && ((next) => setStatus(row.original, next))} />
      ),
    },
    // No delete: no endpoint hard-deletes a brand (only `deactivateBrand`, which is its own named
    // button on the detail page, behind a ReasonDialog — not a row-list trash icon). The detail
    // screen IS the edit form, so its action is a pencil — or "View" with an
    // eye for a read-only role, since the detail page then renders read-only.
    {
      id: "actions",
      header: "",
      size: 110,
      cell: ({ row }) => (
        <RowActions
          name={row.original.name}
          primary={{ label: setStatus ? "Edit brand" : "View brand", icon: setStatus ? PencilIcon : EyeIcon, to: brandPath(row.original.slug) }}
          more={[{ label: "Products of this brand", icon: ProductsIcon, to: `/catalogue/products?brandId=${row.original.id}` }]}
        />
      ),
    },
  ];
}
